import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Spinner, Badge, Modal, InputGroup } from 'react-bootstrap';
import { useApi } from '../../contexts/ApiProvider';
import { useNavigate } from 'react-router-dom';

export default function GearCloset() {
    const api = useApi();
    const navigate = useNavigate();

    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // --- NEW: Filter States ---
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('All');
    const [travelerFilter, setTravelerFilter] = useState('All');

    const [newItem, setNewItem] = useState({ name: '', category: 'Clothing', default_qty: 1, default_weight: '', traveler_name: 'Shared' });
    const [showEdit, setShowEdit] = useState(false);
    const [editItem, setEditItem] = useState({ id: null, name: '', category: '', default_qty: 1, default_weight: '', traveler_name: 'Shared' });

    // --- Shared Constants ---
    const categories = ['Clothing', 'Toiletries', 'Electronics', 'Documents', 'Health', 'Activity Gear', 'Snacks/Food', 'Misc', 'Jewelry'];
    const travelers = ['Shared', 'Deepak', 'Amy'];

    useEffect(() => {
        fetchItems();
        // eslint-disable-next-line
    }, []);

    const fetchItems = async () => {
        setIsLoading(true);
        const res = await api.get('/travel/master-items?per_page=100');
        if (res.ok) setItems(res.body.items || []);
        setIsLoading(false);
    };

    const handleAddItem = async (e) => {
        e.preventDefault();
        const res = await api.post('/travel/master-items', {
            ...newItem,
            default_qty: parseFloat(newItem.default_qty) || 1,
            default_weight: parseFloat(newItem.default_weight) || 0
        });

        if (res.ok) {
            setNewItem({ ...newItem, name: '', default_weight: '' });
            fetchItems();
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Permanently delete this from your templates?")) return;
        const res = await api.delete(`/travel/master-items/${id}`);
        if (res.ok) fetchItems();
    };

    const handleUpdateItem = async (e) => {
        e.preventDefault();
        const res = await api.put(`/travel/master-items/${editItem.id}`, {
            name: editItem.name,
            category: editItem.category,
            default_qty: parseFloat(editItem.default_qty) || 1,
            default_weight: parseFloat(editItem.default_weight) || 0,
            traveler_name: editItem.traveler_name
        });

        if (res.ok) {
            setShowEdit(false);
            fetchItems();
        } else {
            alert("Failed to update item.");
        }
    };

    const handleToggleArchive = async (item) => {
        const res = await api.put(`/travel/master-items/${item.id}`, {
            is_active: !item.is_active
        });
        if (res.ok) fetchItems();
    };

    // --- NEW: Apply Filters ---
    const applyFilters = (itemList) => {
        return itemList.filter(item => {
            const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesCat = categoryFilter === 'All' || item.category === categoryFilter;
            const matchesTraveler = travelerFilter === 'All' || item.traveler_name === travelerFilter;
            return matchesSearch && matchesCat && matchesTraveler;
        });
    };

    const activeItems = applyFilters(items.filter(i => i.is_active !== false));
    const archivedItems = applyFilters(items.filter(i => i.is_active === false));

    const groupedItems = categories.reduce((acc, cat) => {
        acc[cat] = activeItems.filter(item => item.category === cat);
        return acc;
    }, {});
    const otherItems = activeItems.filter(item => !categories.includes(item.category));
    if (otherItems.length > 0) groupedItems['Other'] = otherItems;

    return (
        <Container className="py-4 fade-in">
            <Button variant="link" className="text-muted p-0 mb-3 text-decoration-none" onClick={() => navigate('/travel')}>
                <i className="bi bi-arrow-left me-2"></i>Back to Command Center
            </Button>
            <div className="mb-4">
                <h2 className="fw-bold mb-1">Master Gear Closet</h2>
                <p className="text-muted">Manage your permanent packing templates.</p>
            </div>

            <Row className="g-4">
                {/* Left Column: New Item Form */}
                <Col md={4}>
                    <Card className="border-0 shadow-sm rounded-4 mb-4" style={{ top: window.innerWidth >= 768 ? '80px' : 'auto', position: window.innerWidth >= 768 ? 'sticky' : 'relative' }}>
                        <Card.Body className="p-4">
                            <h6 className="fw-bold mb-3 border-bottom pb-2">Add New Template</h6>
                            <Form onSubmit={handleAddItem}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="small fw-bold text-muted mb-1">Item Name</Form.Label>
                                    <Form.Control type="text" placeholder="e.g., Trail Running Shoes" value={newItem.name} onChange={e => setNewItem({...newItem, name: e.target.value})} required />
                                </Form.Group>
                                <Row className="mb-3 g-2">
                                    <Col>
                                        <Form.Label className="small fw-bold text-muted mb-1">Category</Form.Label>
                                        <Form.Select value={newItem.category} onChange={e => setNewItem({...newItem, category: e.target.value})}>
                                            {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                        </Form.Select>
                                    </Col>
                                    <Col>
                                        <Form.Label className="small fw-bold text-muted mb-1">Assigned To</Form.Label>
                                        <Form.Select value={newItem.traveler_name} onChange={e => setNewItem({...newItem, traveler_name: e.target.value})}>
                                            {travelers.map(t => <option key={t} value={t}>{t}</option>)}
                                        </Form.Select>
                                    </Col>
                                </Row>
                                <Row className="mb-4 g-2">
                                    <Col>
                                        <Form.Label className="small fw-bold text-muted mb-1">Default Qty</Form.Label>
                                        <Form.Control type="number" min="0.5" step="0.5" value={newItem.default_qty} onChange={e => setNewItem({...newItem, default_qty: e.target.value})} />
                                    </Col>
                                    <Col>
                                        <Form.Label className="small fw-bold text-muted mb-1">Weight (lbs)</Form.Label>
                                        <Form.Control type="number" step="0.1" placeholder="0.0" value={newItem.default_weight} onChange={e => setNewItem({...newItem, default_weight: e.target.value})} />
                                    </Col>
                                </Row>
                                <Button variant="primary" type="submit" className="w-100 fw-bold rounded-pill">
                                    <i className="bi bi-plus-lg me-2"></i>Save to Closet
                                </Button>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Right Column: Item Lists */}
                <Col md={8}>
                    {/* --- NEW: Filters Bar --- */}
                    <Card className="border-0 shadow-sm rounded-4 mb-4 bg-light">
                        <Card.Body className="p-3">
                            <Row className="g-2">
                                <Col md={5}>
                                    <InputGroup>
                                        <InputGroup.Text className="bg-white border-end-0"><i className="bi bi-search text-muted"></i></InputGroup.Text>
                                        <Form.Control
                                            className="border-start-0"
                                            placeholder="Search closet..."
                                            value={searchTerm}
                                            onChange={e => setSearchTerm(e.target.value)}
                                        />
                                    </InputGroup>
                                </Col>
                                <Col md={4}>
                                    <Form.Select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
                                        <option value="All">All Categories</option>
                                        {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                    </Form.Select>
                                </Col>
                                <Col md={3}>
                                    <Form.Select value={travelerFilter} onChange={e => setTravelerFilter(e.target.value)}>
                                        <option value="All">All Travelers</option>
                                        {travelers.map(t => <option key={t} value={t}>{t}</option>)}
                                    </Form.Select>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {isLoading ? (
                        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
                    ) : items.length === 0 ? (
                        <div className="text-center py-5 text-muted border rounded-4 bg-light">
                            <i className="bi bi-inbox display-4 mb-3 opacity-50 d-block"></i>
                            <h5>Your closet is empty.</h5>
                        </div>
                    ) : activeItems.length === 0 && archivedItems.length === 0 ? (
                        <div className="text-center py-5 text-muted border rounded-4 bg-light">
                            <p className="mb-0">No items match your filters.</p>
                        </div>
                    ) : (
                        <>
                            {Object.entries(groupedItems).map(([catName, catItems]) => {
                                if (catItems.length === 0) return null;
                                return (
                                    <div key={catName} className="mb-4">
                                        <h6 className="fw-bold text-uppercase letter-spacing-1 text-muted mb-2 border-bottom pb-2">
                                            {catName} <Badge bg="secondary" className="ms-2 rounded-pill">{catItems.length}</Badge>
                                        </h6>
                                        <Table hover responsive borderless className="align-middle bg-white rounded-3 shadow-sm overflow-hidden">
                                            <tbody>
                                                {catItems.map(item => (
                                                    <tr key={item.id} className="border-bottom">
                                                        <td className="fw-bold ps-3">
                                                            {item.name}
                                                            {item.traveler_name && item.traveler_name !== 'Shared' && (
                                                                <Badge bg={item.traveler_name === 'Deepak' ? 'primary' : 'info'} className="ms-2 rounded-pill bg-opacity-75 text-white">
                                                                    {item.traveler_name}
                                                                </Badge>
                                                            )}
                                                        </td>
                                                        <td className="text-muted small">Qty: {item.default_qty}</td>
                                                        <td className="text-muted small d-none d-md-table-cell">{item.default_weight > 0 ? `${item.default_weight} lbs` : '--'}</td>
                                                        <td className="text-end pe-3">
                                                            <Button variant="link" className="text-primary p-0 me-3" title="Edit" onClick={() => {
                                                                setEditItem(item);
                                                                setShowEdit(true);
                                                            }}>
                                                                <i className="bi bi-pencil-square hover-scale"></i>
                                                            </Button>
                                                            <Button variant="link" className="text-warning p-0 me-3" title="Archive" onClick={() => handleToggleArchive(item)}>
                                                                <i className="bi bi-archive hover-scale"></i>
                                                            </Button>
                                                            <Button variant="link" className="text-danger p-0" title="Delete" onClick={() => handleDelete(item.id)}>
                                                                <i className="bi bi-trash3 hover-scale"></i>
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                );
                            })}

                            {archivedItems.length > 0 && (
                                <div className="mt-5 pt-4 border-top">
                                    <h6 className="fw-bold text-uppercase letter-spacing-1 text-muted mb-3">
                                        <i className="bi bi-archive me-2"></i>Archived Gear
                                    </h6>
                                    <Table hover responsive borderless className="align-middle opacity-75 bg-light rounded-4 overflow-hidden shadow-sm">
                                        <tbody>
                                            {archivedItems.map(item => (
                                                <tr key={item.id} className="border-bottom">
                                                    <td className="fw-bold ps-3 text-muted text-decoration-line-through">
                                                        {item.name}
                                                        {item.traveler_name && item.traveler_name !== 'Shared' && (
                                                            <Badge bg="secondary" className="ms-2 rounded-pill bg-opacity-50 text-white">
                                                                {item.traveler_name}
                                                            </Badge>
                                                        )}
                                                    </td>
                                                    <td className="text-muted small">{item.category}</td>
                                                    <td className="text-muted small">Qty: {item.default_qty}</td>
                                                    <td className="text-end pe-3">
                                                        <Button variant="link" className="text-success p-0 me-3" title="Restore to Active" onClick={() => handleToggleArchive(item)}>
                                                            <i className="bi bi-arrow-counterclockwise hover-scale"></i>
                                                        </Button>
                                                        <Button variant="link" className="text-danger p-0" title="Permanently Delete" onClick={() => handleDelete(item.id)}>
                                                            <i className="bi bi-trash3 hover-scale"></i>
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </>
                    )}
                </Col>
            </Row>

            <Modal show={showEdit} onHide={() => setShowEdit(false)} centered fullscreen="md-down">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold">Edit Item</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleUpdateItem}>
                        <Form.Group className="mb-3">
                            <Form.Label className="small fw-bold text-muted mb-1">Item Name</Form.Label>
                            <Form.Control type="text" value={editItem.name} onChange={e => setEditItem({...editItem, name: e.target.value})} required />
                        </Form.Group>
                        <Row className="mb-3 g-2">
                            <Col>
                                <Form.Label className="small fw-bold text-muted mb-1">Category</Form.Label>
                                <Form.Select value={editItem.category} onChange={e => setEditItem({...editItem, category: e.target.value})}>
                                    {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                    {editItem.category && !categories.includes(editItem.category) && <option value={editItem.category}>{editItem.category}</option>}
                                </Form.Select>
                            </Col>
                            <Col>
                                <Form.Label className="small fw-bold text-muted mb-1">Assigned To</Form.Label>
                                <Form.Select value={editItem.traveler_name || 'Shared'} onChange={e => setEditItem({...editItem, traveler_name: e.target.value})}>
                                    {travelers.map(t => <option key={t} value={t}>{t}</option>)}
                                </Form.Select>
                            </Col>
                        </Row>
                        <Row className="mb-4 g-2">
                            <Col>
                                <Form.Label className="small fw-bold text-muted mb-1">Default Qty</Form.Label>
                                <Form.Control type="number" min="0.5" step="0.5" value={editItem.default_qty} onChange={e => setEditItem({...editItem, default_qty: e.target.value})} />
                            </Col>
                            <Col>
                                <Form.Label className="small fw-bold text-muted mb-1">Weight (lbs)</Form.Label>
                                <Form.Control type="number" step="0.1" value={editItem.default_weight} onChange={e => setEditItem({...editItem, default_weight: e.target.value})} />
                            </Col>
                        </Row>
                        <Button variant="primary" type="submit" className="w-100 fw-bold rounded-pill">Save Changes</Button>
                    </Form>
                </Modal.Body>
            </Modal>
        </Container>
    );
}
